import { ApiError, apiFetch, ensureFreshAccessToken, getAccessToken, resolveApiBase, } from '@/lib/api/client';
/**
 * DEPRECATED lane, kept as the fallback while the grant endpoint rolls out:
 * puts the user's full JWT in the socket URL as ?access_token=. Prefer
 * resolveSttSocketTarget(), which uses a short-lived relay ticket instead.
 */
export async function buildSttWebSocketUrl() {
    // Refresh first: the WS handshake carries the access token as a query param
    // and never flows through apiFetch, so an idle-but-refreshable session would
    // otherwise open with a stale token and get a 401 from /ws/stt.
    await ensureFreshAccessToken();
    const apiBase = resolveApiBase('/ws/stt');
    const wsBase = apiBase.replace(/^http/i, 'ws');
    const token = getAccessToken();
    const qs = token ? `?access_token=${encodeURIComponent(token)}` : '';
    return `${wsBase}/ws/stt${qs}`;
}
/**
 * Mints a short-lived relay ticket for /ws/stt. The ticket (not the JWT) rides
 * in the socket URL, so the long-lived credential stays out of proxy and
 * access logs; it is bound to this user and expires in ~60 seconds, so it must
 * be fetched immediately before opening the socket.
 */
export async function fetchSttSessionGrant() {
    return apiFetch('/stt/session-grants', { method: 'POST' });
}
export function buildSttWebSocketUrlFromGrant(grant) {
    const relayPath = grant.relayPath || '/ws/stt';
    const apiBase = resolveApiBase(relayPath);
    const wsBase = apiBase.replace(/^http/i, 'ws');
    return `${wsBase}${relayPath}?ticket=${encodeURIComponent(grant.ticket)}`;
}
/**
 * Resolves where the STT socket should connect. Preferred: a session grant
 * (ticket in the URL, JWT never leaves the Authorization header). Only when
 * the grant endpoint does not exist (404/405 from an older backend during
 * rollout) does it fall back to the legacy ?access_token= URL so dictation
 * keeps working; the dsid is then generated client-side. Any other failure
 * (403 "dictation not enabled", 5xx, network) is rethrown as-is so the caller
 * sees the server's message and status instead of a misleading socket error.
 */
export async function resolveSttSocketTarget() {
    try {
        const grant = await fetchSttSessionGrant();
        return { wsUrl: buildSttWebSocketUrlFromGrant(grant), dsid: grant.sessionId };
    }
    catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
            return { wsUrl: await buildSttWebSocketUrl(), dsid: crypto.randomUUID() };
        }
        throw err;
    }
}
/**
 * Returns the STT providers known to the server, each flagged with whether its
 * API key is configured. The dictation UI uses this to pick a provider/model
 * that actually has a key instead of hard-coding Deepgram.
 */
export async function getSttProviders() {
    return apiFetch('/stt/providers');
}
